import { create } from 'zustand';
import type { Notification } from '@perfin/shared';
import type { UserRole } from '@perfin/shared';
import {
  subscribeNotifications,
  clearNotification as clearNotificationService,
  clearNotificationsByIds as clearNotificationsByIdsService,
} from '@perfin/shared';

interface NotificationState {
  notifications: Notification[];
  loading: boolean;
  error: string | null;
  subscribedUserId: string | null; // Track active subscription for idempotency

  // Actions
  subscribeToNotifications: (userId: string, orgId: string, role: UserRole) => () => void;
  clearNotification: (notificationId: string, recipientUserId: string) => Promise<void>;
  clearNotificationsByIds: (notifications: Array<{ id: string; recipientUserId: string }>) => Promise<void>;
  clearError: () => void;
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],
  loading: false,
  error: null,
  subscribedUserId: null,

  subscribeToNotifications: (userId: string, orgId: string, role: UserRole) => {
    // Idempotency: prevent duplicate subscriptions for same user
    if (get().subscribedUserId === userId) {
      return () => {}; // Already subscribed, return no-op
    }
    set({ subscribedUserId: userId, loading: true });
    return subscribeNotifications(userId, orgId, role, (notifications) => {
      set({ notifications, loading: false });
    });
  },

  clearNotification: async (notificationId: string, recipientUserId: string) => {
    try {
      await clearNotificationService(notificationId, recipientUserId);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to clear notification';
      set({ error: errorMessage });
      throw error;
    }
  },

  clearNotificationsByIds: async (notifications: Array<{ id: string; recipientUserId: string }>) => {
    try {
      await clearNotificationsByIdsService(notifications);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to clear notifications';
      set({ error: errorMessage });
      throw error;
    }
  },

  clearError: () => set({ error: null }),
}));
